import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import {
  fetchUtility,
  fetchConstellationPlans,
  fetchPlan,
} from "../../../fetch/fetch";
import { useZipCode } from "../ZipCodeContext";
import { PROVIDER_ID, PROVIDER_NAME, REPs, ThreeReps, Tier } from "../../../config";
import { Comparison, PlanProps } from "../../../types";
import { getCurrentPromoCode } from "../../../utils/promoCodeUtils";
// import { useNavigate } from "react-router-dom";
export interface Plan {
  plan_id: string;
  plan_name: string;
  provider_id: number;
  price1000: number;
  contract_term: number;
  features: string[];
  savings?: string;
  price: string;
  yrac?: string;
  terms_url: string;
  efl_url: string;
  cancel_fee?: string;
  cancel_fee_type?: string;
  badge?: string; // this value will be added in the FE
  earlyTerminationFee?: string; // add this for direct mapping
  product: unknown;
}
interface PlanContextType {
  plans: Plan[];
  setPlans: (plans: Plan[]) => void;
  plansLoading: boolean;
  zipCode: string;
  setZipCode: (zip: string) => void;
  selectedPlan: Plan | undefined;
  setSelectedPlan: Function;
  utility: { utility_code?: string } | null;
  compareData: any[];
  setCompareData: (data: any[]) => void;
  competitorPrices: any;
  setCompetitorPrices: (data: any) => void;
  comparePlansLoading: boolean;
  setComparePlansLoading: (loading: boolean) => void;
  bestValuePlan: Plan | null;
  setBestValuePlan: (plan: Plan | null) => void;
  locationDenied: boolean;
  saveZipToStorage: (zip: string) => void;
  zipErrorModal: boolean;
  setZipErrorModal: (show: boolean) => void;
}

const PlanContext = createContext<PlanContextType | undefined>(undefined);
export const usePlanContext = () => {
  const context = useContext(PlanContext);
  if (!context)
    throw new Error("usePlanContext must be used within PlanProvider");
  return context;
};

export const PlanProvider = ({ children }: { children: ReactNode }) => {
  const routeBasedPromoCode = getCurrentPromoCode(window.location.pathname);
  const { zipCode, setZipCode } = useZipCode();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan>();
  const [plansLoading, setPlansLoading] = useState<boolean>(true);
  const [comparePlansLoading, setComparePlansLoading] =
    useState<boolean>(false);
  const [utility, setUtility] = useState<{ utility_code?: string } | null>(
    null,
  );
  const avgMonthlyUse = "1000";
  const [compareData, setCompareData] = useState<any[]>([]);
  const [competitorPrices, setCompetitorPrices] = useState<any>(null);
  const [bestValuePlan, setBestValuePlan] = useState<Plan | null>(null);
  // const [promoCode, setPromoCode] = useState<string>();
  const [locationDenied, setLocationDenied] = useState(false);
  const [zipErrorModal, setZipErrorModal] = useState(false);
  //fetch ZIP code based on IP address using ipapi.co (free, no API key required)
  //  const detectLocation = async () => {
  //   try {
  //     const response = await fetch("https://ipapi.co/json/");
  //     const data = await response.json();
  //     if (data && data.postal) {
  //       const zipcode = data.postal.replace(/\D/g, "").slice(0, 5);
  //       if (zipcode.length === 5) {
  //         setZipCode(zipcode);
  //       } else {
  //         console.error("Could not detect a valid ZIP code from your IP location");
  //         setPlansLoading(false);
  //       }
  //     } else {
  //       console.error("Could not retrieve ZIP code from your IP location");
  //       setPlansLoading(false);
  //     }
  //   } catch (err) {
  //     console.error("Error fetching IP location data:", err);
  //     setPlansLoading(false);
  //   }
  // };

  // Helper functions to get/set ZIP code in cookies for 7 days
  const ZIP_COOKIE_NAME = "user_zip";
  const SEVEN_DAYS = 7;
  // Wait for OneTrust to fully load and initialize
  const waitForOneTrust = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if ((window as any).OnetrustActiveGroups) {
        resolve(true);
        return;
      }
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait
      const checkInterval = setInterval(() => {
        attempts++;
        if ((window as any).OnetrustActiveGroups) {
          clearInterval(checkInterval);
          resolve(true);
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          resolve(false);
        }
      }, 100);
    });
  };
  // Save ZIP cookie only if OneTrust consent is granted
  async function saveZipToStorage(zip: string) {
    // Check if OneTrust SDK is loaded
    if (typeof (window as any).OneTrust === "undefined") {
      return;
    }
    // Wait for OneTrust to fully initialize
    await waitForOneTrust();
    const oneTrustGroups = (window as any).OnetrustActiveGroups;
    // If groups are undefined or empty, user denied consent
    if (!oneTrustGroups || oneTrustGroups === "") {
      return;
    }
    // Check for Performance (C0002) or Functional (C0003) cookies consent
    const groupsString = String(oneTrustGroups);
    const hasConsent =
      groupsString.includes("C0002") || groupsString.includes("C0003");
    if (!hasConsent) {
      return;
    }
    // Save ZIP with 7-day expiry
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + SEVEN_DAYS);
    document.cookie = `${ZIP_COOKIE_NAME}=${zip}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax; Secure`;
  }
  // Read ZIP cookie
  function getZipFromStorage(): string | null {
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === ZIP_COOKIE_NAME) {
        return value || null;
      }
    }
    return null;
  }
  // Remove ZIP cookie when consent is withdrawn
  function removeZipCookie() {
    document.cookie = `${ZIP_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
  }
  // OneTrust callback - runs when SDK loads
  (window as any).OptanonWrapper = function () {
    // OneTrust is now loaded and initialized
  };
  // OneTrust callback - runs when user changes consent preferences
  (window as any).OptanonActiveGroupsChanged = function () {
    const activeGroups = (window as any).OnetrustActiveGroups || "";
    const hasOptionalCookies = activeGroups.includes("C0002") || activeGroups.includes("C0003");

    if (!hasOptionalCookies) {
      removeZipCookie();
    }
  };
  //fetch ZIP code based on IP address take user permission using browser geolocation API
  const detectLocation = async () => {
    try {
      // First check cookieStorage
      const storedZip = getZipFromStorage();
      if (storedZip) {
        setZipCode(storedZip);
        return;
      }
      // Fallback to geolocation
      const zip = await getZIPCode();
      if (zip) {
        setZipCode(zip as string);
      }
    } catch (err) {
      console.error("Error detecting location:", err);
      setPlansLoading(false);
    }
  };
  async function getZIPCode() {
    return new Promise((resolve, reject) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            // Use a reverse geocoding service to get ZIP code
            try {
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
              );
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              }
              const data = await response.json();
              const address = data.address;
              const userZipCode = address.postcode || address["postal_code"];

              if (userZipCode) {
                saveZipToStorage(userZipCode);
                resolve(userZipCode);
              } else {
                reject("ZIP code not found.");
              }
            } catch (err) {
              reject(`Error fetching location data: ${err}`);
            }
          },
          (error) => {
            if (error.code === error.PERMISSION_DENIED) {
              setLocationDenied(true); // <-- IMPORTANT
            }
            reject(`Error getting location: ${error.message}`);
          },
        );
      } else {
        reject("Geolocation is not supported in your browser.");
      }
    });
  }

  // Auto-detect location if no ZIP code is set
  useEffect(() => {
    if (!zipCode) {
      detectLocation();
    } else {
      fetchPlansData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipCode]);
  //console.log("Stored ZIP:", getZipFromStorage());
  // We van set menually promo code for testing
  //useEffect(() => { setPromoCode("EIQPPC"); localStorage.setItem("promoCode", "EIQPPC"); }, []);

  // Remove any existing addresses,plans in the local storage if user come back to the plan selection page from enrollment page
  useEffect(() => {
    localStorage.removeItem("selectedAddress");
    localStorage.removeItem("ts_plan");
  }, []);
  const fetchPlansData = useCallback(async () => {
    if (!zipCode || zipCode.length !== 5) {
      setPlansLoading(false);
      return;
    }
    setPlansLoading(true);
    try {
      // Fetch utility Data based on ZIP code
      const utilityData = await fetchUtility(zipCode);
      if (!utilityData && utilityData?.errorCode == "Invalid Zipcode.") {
        setZipErrorModal(true);
        setCompareData([]);
        setPlans([]);
        setPlansLoading(false);
        return;
      }
      if (!utilityData || !utilityData[0] || !utilityData[0].utility_code) {
        setUtility(null);
        setPlans([]);
        setPlansLoading(false);
        return;
      }
      setUtility(utilityData[0]);
      const utilityCode = utilityData[0].utility_code;
      // Fetch constellation plans for each contract_terms 6,12,24,36
      let allPlans: any[] = [];
      let compare_data: Comparison[] = [];
      let constoprice = 0;
      const usages = new Array(12).fill(1000);
      const response = await fetchConstellationPlans(
        zipCode,
        utilityCode,
        [6, 12, 24, 36],
        PROVIDER_ID,
        usages,
        routeBasedPromoCode,
      );
      if (
        response &&
        Number(response.status) === 1 &&
        response.response &&
        Array.isArray(response.response.plans)
      ) {
        const plans = response.response.plans;
        allPlans = allPlans.concat(plans);
      }
      if (allPlans.length > 0) {
        // Find the lowest price 12-month plan, fallback to overall lowest if none
        const twelveMonthPlans = allPlans.filter(
          (plan) => plan.contract_term === 12 || plan.contract_term === "12",
        );
        const lowestConstoPlan =
          twelveMonthPlans.length > 0
            ? twelveMonthPlans.reduce((minPlan, currentPlan) => {
                return parseFloat(currentPlan.price1000) <
                  parseFloat(minPlan.price1000)
                  ? currentPlan
                  : minPlan;
              }, twelveMonthPlans[0])
            : allPlans.reduce((minPlan, currentPlan) => {
                return parseFloat(currentPlan.price1000) <
                  parseFloat(minPlan.price1000)
                  ? currentPlan
                  : minPlan;
              }, allPlans[0]);
        constoprice = Number(lowestConstoPlan.price1000);
        // the first Plan is always Constellation
        compare_data.push({
          value: constoprice,
          unit: "¢",
          color: "#2372B9",
          logoSrc: "/images/constenewImg/compareCClogo.svg",
          plan_name: lowestConstoPlan.plan_name,
          yrac: lowestConstoPlan.yrac,
          terms_url: lowestConstoPlan.terms_url,
          efl_url: lowestConstoPlan.efl_url,
          plan_id: lowestConstoPlan.plan_id,
          contract_term: lowestConstoPlan.contract_term,
        });
      }
      // Fetch competitor prices for comparison
      const competitorRes = await fetchPlan(12, utilityCode, usages);
      if (competitorRes?.response) {
        Object.keys(competitorRes.response).forEach((providerId) => {
          if (providerId === "196") return; // 196 is constellation itself, we inject it manually
          if (providerId === "176") {
            // 176 is TXU
            const plansArr = competitorRes.response[providerId];
            // Find the Plan name called 'Saver's Choice 12' and push it to compareData
            let saversChoice = plansArr.find(
              (plan: any) =>
                plan.plan_name.toLowerCase() === "saver's choice 12",
            );
            // console.log({ saversChoice }, "TXU")
            if (saversChoice && saversChoice.price1000 > constoprice) {
              const price = Number(saversChoice.price1000);
              compare_data.push({
                value: price,
                unit: "¢",
                color: "#D9E6F4",
                logoSrc: `https://www.powerlego.com/ApiGateway/v2/logo/provider/${providerId}`,
                plan_name: saversChoice.plan_name,
                yrac: saversChoice.yrac,
                terms_url: saversChoice.terms_url,
                efl_url: saversChoice.efl_url,
                plan_id: saversChoice.plan_id,
                contract_term: saversChoice.contract_term,
              });
            }
          }
          // go through all other providers and inject the lowest plan
          else {
            const plansArr = competitorRes.response[providerId];
            let lowestPlan = plansArr.reduce(
              (minPlan: any, currentPlan: any) => {
                return parseFloat(currentPlan.price1000) <
                  parseFloat(minPlan.price1000)
                  ? currentPlan
                  : minPlan;
              },
              plansArr[0],
            );
            const price = Number(lowestPlan.price1000);
            if (price > constoprice) {
              compare_data.push({
                value: price,
                unit: "¢",
                color: "#D9E6F4",
                logoSrc: `https://www.powerlego.com/ApiGateway/v2/logo/provider/${providerId}`,
                plan_name: lowestPlan.plan_name,
                 terms_url: lowestPlan.terms_url,
                efl_url: lowestPlan.efl_url,
                plan_id: lowestPlan.plan_id,
                contract_term: lowestPlan.contract_term,
                yrac: lowestPlan.yrac,
              });
            }
          }
        });
        // console.log({ compare_data })
        // in compare_data, first object is always constellation
        // go through the rest of the data and sort the other objects from highest to lowest price
        const constellationPlan = compare_data[0];
        const otherPlans = compare_data.slice(1);
        otherPlans.sort((a, b) => (b.value || 0) - (a.value || 0));
        compare_data = [constellationPlan, ...otherPlans];
        compare_data = compare_data.slice(0, 3);
        setCompareData(compare_data);
      }
      // const highestRate = Math.max(...(compare_data || {}).map((item: { value: any; }) => item.value || 0))
      const highestRate = compare_data.reduce(
        (max, item) => (item.value && item.value > max ? item.value : max),
        0,
      );
      // fetch and show constellation plans for the const plan cards
      if (allPlans.length > 0) {
        // Transform API data to match component structure
        const transformedPlans = allPlans.map((plan: any) => {
          const rateDiff = highestRate - plan.price1000;
          const savings =
            (rateDiff *
              parseFloat(avgMonthlyUse) *
              parseInt(plan.contract_term)) /
            100;
          // Determine features based on contract_term and plan_name
          let features: string[] = [];
          const isProtectPlus =
            plan.plan_name &&
            typeof plan.plan_name === "string" &&
            plan.plan_name.toLowerCase().includes("protect");
          if (isProtectPlus) {
            features = [
              "Breakdown coverage of your HVAC system and Water Heater",
              "Performance monitoring of your A/C system",
              "Receive up to $50 per month in bill credits",
              "Fixed-rate electricity plan",
            ];
          } else {
            features = [
              "Receive up to $50 per month in bill credits",
              "Fixed-rate electricity plan",
            ];
          }
          return {
            plan_id: plan.plan_id,
            plan_name: plan.plan_name,
            provider_id: plan.provider_id,
            price500: plan.price500,
            price1000: plan.price1000,
            price2000: plan.price2000,
            price: plan.price1000, // Use in plan details modal
            contract_term: plan.contract_term,
            cancel_fee: plan.cancel_fee || plan.earlyTerminationFee,
            cancel_fee_type: plan.cancel_fee_type,
            earlyTerminationFee: plan.earlyTerminationFee,
            provider_name: plan.source,
            features,
            // savings: `$${savings}`,
            ...(savings > 0 ? { savings: `$${savings.toFixed(0)}` } : {}),
            highlight: false,
            showComparison: false,
            yrac: plan.yrac,
            terms_url: plan.terms_url,
            efl_url: plan.efl_url,
            apiData: plan,
            product: plan.product ?? {}, // Ensure product property exists
            promo_code: routeBasedPromoCode,
          };
        });
        // Find the lowest price 12-month plan, fallback to overall lowest if none
        const twelveMonthPlans = transformedPlans.filter(
          (plan) => plan.contract_term === 12 || plan.contract_term === "12",
        );
        const constobestValue =
          twelveMonthPlans.length > 0
            ? twelveMonthPlans.reduce((minPlan, currentPlan) => {
                return parseFloat(currentPlan.price1000) <
                  parseFloat(minPlan.price1000)
                  ? currentPlan
                  : minPlan;
              }, twelveMonthPlans[0])
            : transformedPlans.reduce((minPlan, currentPlan) => {
                return parseFloat(currentPlan.price1000) <
                  parseFloat(minPlan.price1000)
                  ? currentPlan
                  : minPlan;
              }, transformedPlans[0]);

        setBestValuePlan(constobestValue);
        setPlans(transformedPlans);
      } else {
        setPlans([]);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, [zipCode, avgMonthlyUse, routeBasedPromoCode]);
  return (
    <PlanContext.Provider
      value={{
        plans,
        setPlans,
        plansLoading,
        comparePlansLoading,
        setComparePlansLoading,
        zipCode,
        setZipCode,
        setSelectedPlan,
        selectedPlan,
        utility,
        compareData,
        setCompareData,
        competitorPrices,
        setCompetitorPrices,
        bestValuePlan,
        setBestValuePlan,
        locationDenied,
        saveZipToStorage,
        zipErrorModal,
        setZipErrorModal,
      }}
    >
      {children}
    </PlanContext.Provider>
  );
};
